/**
 * The Settings popup (UI-004): which way round the table is drawn, how big, and
 * the theme, and the sound effects volume (SND-001).
 *
 * A native `<dialog>` opened with `showModal()`, which gives focus trapping, Esc to
 * close, an inert page behind it and the right semantics for screen readers without
 * any code of ours. Changes apply as they are made; there is no Save button to miss.
 *
 * Every string comes from `config/site.ts` (CLAUDE.md §6).
 */
import { SETTINGS_COPY } from '../config/site.ts';
import { DEFAULT_OFFER, type Offer } from '../settings/settings.ts';
import {
  CUE_LENGTH_MAX,
  CUE_LENGTH_MIN,
  DEFAULT_SETTINGS,
  TABLE_SCALE_MAX,
  TABLE_SCALE_MIN,
  parseSettings,
  type Settings,
  type TableOrientation,
} from '../settings/settings.ts';

export interface SettingsDialog {
  open(): void;
  destroy(): void;
}

const ORIENTATION_ORDER: readonly TableOrientation[] = ['landscape', 'portrait'];

const percent = (fraction: number): string => String(Math.round(fraction * 100));

export interface SettingsDialogOptions {
  /**
   * On a phone the table follows how the phone is held (UI-011), so the orientation
   * choice is replaced by a line saying so. The saved setting is kept, untouched, for
   * the day the same account plays on a tablet.
   */
  readonly followsDevice?: boolean;
  /** Which themes and cue styles to offer (UI-019: all of them in debug mode). */
  readonly offer?: Offer;
}

export function createSettingsDialog(
  host: HTMLElement,
  initial: Settings,
  onChange: (next: Settings) => void,
  options: SettingsDialogOptions = {},
): SettingsDialog {
  const c = SETTINGS_COPY;
  const dialog = document.createElement('dialog');
  dialog.className = 'settings';
  dialog.setAttribute('aria-labelledby', 'settings-title');

  const orientationOptions = ORIENTATION_ORDER.map(
    (value) => `
      <label class="segmented__option">
        <input type="radio" name="orientation" value="${value}" />
        <span>${c.orientations[value]}</span>
      </label>`,
  ).join('');

  /*
   * Each swatch is a tiny table wearing its own theme: the `data-theme` attribute on
   * the swatch makes the tokens inside it resolve to that theme, so the preview is
   * drawn in the theme's real colours without this file naming a single one.
   */
  const offer = options.offer ?? DEFAULT_OFFER;
  const themeOptions = offer.themes
    .map(
      (theme) => `
      <label class="theme-option">
        <input type="radio" name="theme" value="${theme.id}" />
        <span class="theme-option__swatch" data-theme="${theme.id}" aria-hidden="true">
          <span class="theme-option__cloth"><span class="theme-option__ball"></span></span>
        </span>
        <span class="theme-option__name">${theme.name}${
          theme.tier === 'premium' ? ` <span class="theme-option__tier">${c.premium}</span>` : ''
        }</span>
      </label>`,
    )
    .join('');

  /* UI-019: each swatch is a little cue in its own style, by its own data-cue. */
  const cueOptions = offer.cues
    .map(
      (cue) => `
      <label class="cue-option">
        <input type="radio" name="cueStyle" value="${cue.id}" />
        <span class="cue-option__swatch" data-cue="${cue.id}" aria-hidden="true"></span>
        <span class="theme-option__name">${cue.name}${
          cue.tier === 'premium' ? ` <span class="theme-option__tier">${c.premium}</span>` : ''
        }</span>
      </label>`,
    )
    .join('');

  dialog.innerHTML = `
    <form method="dialog" class="settings__card">
      <header class="settings__header">
        <h2 class="settings__title" id="settings-title">${c.title}</h2>
        <button type="submit" class="btn btn--icon" aria-label="${c.close}">&times;</button>
      </header>

      <!-- ACC-008: Account has its own popup, from the header, beside Settings. -->

      <section class="settings__section">
        <h3 class="settings__heading">${c.tableHeading}</h3>
        <fieldset class="settings__field"${options.followsDevice === true ? ' hidden' : ''}>
          <legend class="settings__label">${c.orientation}</legend>
          <div class="segmented">${orientationOptions}</div>
        </fieldset>
        ${
          options.followsDevice === true
            ? `<p class="settings__field settings__hint">
                 <span class="settings__label">${c.orientation}</span>
                 <span>${c.orientationFollowsPhone}</span>
               </p>`
            : ''
        }
        <label class="settings__field">
          <span class="settings__label">
            ${c.tableScale} <span class="settings__value numeral" data-value="tableScale"></span>
          </span>
          <input class="controls__slider" type="range" data-setting="tableScale"
                 min="${percent(TABLE_SCALE_MIN)}" max="${percent(TABLE_SCALE_MAX)}" step="5" />
        </label>
        <label class="settings__toggle">
          <input type="checkbox" name="showCue" />
          <span>${c.showCue}</span>
        </label>
        <label class="settings__field">
          <span class="settings__label">
            ${c.cueLength} <span class="settings__value numeral" data-value="cueLength"></span>
          </span>
          <input class="controls__slider" type="range" data-setting="cueLength"
                 min="${percent(CUE_LENGTH_MIN)}" max="${percent(CUE_LENGTH_MAX)}" step="5" />
        </label>
        <fieldset class="settings__field">
          <legend class="settings__label">${c.cueStyle}</legend>
          <div class="cue-grid">${cueOptions}</div>
        </fieldset>
      </section>

      <section class="settings__section">
        <h3 class="settings__heading">${c.themeHeading}</h3>
        <fieldset class="settings__field">
          <legend class="settings__label visually-hidden">${c.themeHeading}</legend>
          <div class="theme-grid">${themeOptions}</div>
        </fieldset>
      </section>

      <section class="settings__section">
        <h3 class="settings__heading">${c.soundHeading}</h3>
        <!-- No Music slider until there is music: a control that does nothing misleads.
             The musicVolume setting is kept, so it can come back unchanged. -->
        <label class="settings__field">
          <span class="settings__label">
            ${c.effects} <span class="settings__value numeral" data-value="effectsVolume"></span>
          </span>
          <input class="controls__slider" type="range" data-setting="effectsVolume"
                 min="0" max="100" step="5" />
        </label>
      </section>

      <footer class="settings__footer">
        <button type="submit" class="btn btn--primary">${c.done}</button>
      </footer>
    </form>
  `;
  host.appendChild(dialog);

  let current = initial;
  const sliders = [...dialog.querySelectorAll<HTMLInputElement>('input[data-setting]')];
  const radios = [...dialog.querySelectorAll<HTMLInputElement>('input[name="orientation"]')];
  const themeRadios = [...dialog.querySelectorAll<HTMLInputElement>('input[name="theme"]')];
  const cueRadios = [...dialog.querySelectorAll<HTMLInputElement>('input[name="cueStyle"]')];
  const cueToggle: HTMLInputElement = (() => {
    const toggle = dialog.querySelector<HTMLInputElement>('input[name="showCue"]');
    if (toggle === null) throw new Error('settings: the pool cue toggle is missing');
    return toggle;
  })();

  function render(): void {
    for (const radio of radios) radio.checked = radio.value === current.orientation;
    for (const radio of themeRadios) radio.checked = radio.value === current.theme;
    for (const radio of cueRadios) radio.checked = radio.value === current.cueStyle;
    cueToggle.checked = current.showCue;
    for (const slider of sliders) {
      const key = slider.dataset['setting'];
      if (
        key !== 'tableScale' &&
        key !== 'musicVolume' &&
        key !== 'effectsVolume' &&
        key !== 'cueLength'
      ) {
        continue;
      }
      slider.value = percent(current[key]);
      const readout = dialog.querySelector(`[data-value="${key}"]`);
      if (readout !== null) readout.textContent = `${percent(current[key])}%`;
    }
  }

  /** Read the form back through the same validation as storage — it is input too. */
  function read(): Settings {
    const draft: Record<string, unknown> = {
      orientation: radios.find((radio) => radio.checked)?.value,
      theme: themeRadios.find((radio) => radio.checked)?.value,
      showCue: cueToggle.checked,
      cueStyle: cueRadios.find((radio) => radio.checked)?.value,
    };
    for (const slider of sliders) {
      const key = slider.dataset['setting'];
      if (key !== undefined) draft[key] = Number(slider.value) / 100;
    }
    return parseSettings(draft, DEFAULT_SETTINGS, offer);
  }

  const onInput = (): void => {
    current = read();
    render();
    onChange(current);
  };

  // A click on the backdrop (the dialog element itself, outside the card) closes it.
  const onBackdrop = (event: MouseEvent): void => {
    if (event.target === dialog) dialog.close();
  };

  dialog.addEventListener('input', onInput);
  dialog.addEventListener('click', onBackdrop);
  render();

  return {
    open() {
      render();
      dialog.showModal();
    },
    destroy() {
      dialog.removeEventListener('input', onInput);
      dialog.removeEventListener('click', onBackdrop);
      dialog.remove();
    },
  };
}
